import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AppService from '../../../services/AppService';

/**
 * ServiceMappingDialog component for inline service-partner mapping
 *
 * Shows when admin tries to assign ticket to partner without service mapping
 * Allows quick mapping creation without navigating away from assignment flow
 *
 * @param {Object} props Component props
 * @param {boolean} props.open Whether the dialog is open
 * @param {Function} props.onClose Function to call when the dialog is closed
 * @param {Object} props.service The service object from ticket
 * @param {Object} props.partner The selected partner organization
 * @param {number} props.govtCharge Government charge from customer mapping (read-only)
 * @param {Function} props.onMappingCreated Callback when mapping is successfully created
 */
const ServiceMappingDialog = ({
  open,
  onClose,
  service,
  partner,
  govtCharge = 0,
  onMappingCreated
}) => {
  const calculateServiceAndTaxFromInclusive = (inclusiveAmount) => {
    const inc = parseFloat(inclusiveAmount);
    if (!Number.isFinite(inc)) {
      return { serviceCharge: '', taxOnServiceCharge: '' };
    }

    const rawService = inc / 1.05;
    const serviceCharge = Math.floor(rawService * 100) / 100;
    const tax = inc - serviceCharge;
    const taxOnServiceCharge = Math.round(tax * 100) / 100;

    return {
      serviceCharge: serviceCharge.toFixed(2),
      taxOnServiceCharge: taxOnServiceCharge.toFixed(2),
    };
  };

  const calculateInclusiveFromServiceAndTax = (serviceCharge, taxOnServiceCharge) => {
    const serviceAmount = parseFloat(serviceCharge);
    const taxAmount = parseFloat(taxOnServiceCharge);
    if (!Number.isFinite(serviceAmount) || !Number.isFinite(taxAmount)) return '';
    return (serviceAmount + taxAmount).toFixed(2);
  };

  const [chargeMode, setChargeMode] = useState('FIXED');
  const [percentage, setPercentage] = useState('');
  const [serviceChargeIncTax, setServiceChargeIncTax] = useState('');
  const [serviceCharge, setServiceCharge] = useState('');
  const [taxOnServiceCharge, setTaxOnServiceCharge] = useState('');
  const [serviceDetails, setServiceDetails] = useState(null);
  const [serviceDetailsLoading, setServiceDetailsLoading] = useState(false);
  const [serviceDetailsError, setServiceDetailsError] = useState(null);

  // Loading and error states
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Initialize form with default values when dialog opens
  useEffect(() => {
    if (!open) return;
    setError(null);
    setServiceDetailsError(null);
    setChargeMode('FIXED');
    setPercentage('');
    setServiceChargeIncTax('');
    setServiceCharge('');
    setTaxOnServiceCharge('');

    const serviceId = service?.id;
    if (!serviceId) {
      setServiceDetails(null);
      return;
    }

    setServiceDetailsLoading(true);
    AppService.get(`v1/service/details?serviceId=${serviceId}`)
      .then((details) => {
        setServiceDetails(details);
      })
      .catch((e) => {
        console.error('Failed to load service details:', e);
        setServiceDetails(null);
        setServiceDetailsError('Unable to load default charges for this service.');
      })
      .finally(() => {
        setServiceDetailsLoading(false);
      });
  }, [open, service]);

  const applyInclusiveAmount = (value) => {
    setServiceChargeIncTax(value);
    const { serviceCharge: excl, taxOnServiceCharge: tax } = calculateServiceAndTaxFromInclusive(value);
    setServiceCharge(excl);
    setTaxOnServiceCharge(tax);
  };

  const handleServiceChargeIncTaxChange = (event) => {
    const value = event.target.value;
    if (chargeMode === 'PERCENTAGE') return;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      applyInclusiveAmount(value);
    }
  };

  const handlePercentageChange = (event) => {
    const value = event.target.value;
    if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;
    setPercentage(value);

    if (!serviceDetails) return;
    const defaultInc = calculateInclusiveFromServiceAndTax(serviceDetails.serviceCharge, serviceDetails.taxOnServiceCharge);
    const p = parseFloat(value);
    const base = parseFloat(defaultInc);
    if (!Number.isFinite(p) || !Number.isFinite(base)) return;

    const computedInc = (base * (p / 100)).toFixed(2);
    applyInclusiveAmount(computedInc);
  };

  // Validate form
  const isFormValid = () => {
    if (!serviceChargeIncTax || parseFloat(serviceChargeIncTax) <= 0) {
      setError('Service charge is required and must be greater than 0');
      return false;
    }
    if (!serviceCharge || parseFloat(serviceCharge) <= 0) {
      setError('Invalid service charge amount');
      return false;
    }
    if (!taxOnServiceCharge || parseFloat(taxOnServiceCharge) < 0) {
      setError('Invalid tax amount');
      return false;
    }
    return true;
  };

  // Handle mapping creation
  const handleCreateMapping = async () => {
    // Validate form
    if (!isFormValid()) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      console.log('Creating service-partner mapping...');
      console.log('Partner:', partner);
      console.log('Service:', service);

      // Prepare mapping request
      // Note: Tax is collected from customer and paid to government, not to partner
      // We use the service's tax rate here (required by backend) but partner doesn't set it
      const mappingRequest = {
        organisationId: partner.organisation?.id || partner.organisationId || partner.organization?.id || partner.id,
        serviceId: service.id,
        serviceCharge: parseFloat(serviceCharge),
        taxOnServiceCharge: parseFloat(taxOnServiceCharge || 0),
        govtCharge: parseFloat(govtCharge) || 0
      };

      console.log('Mapping request:', mappingRequest);

      // Call API to create mapping
      const response = await AppService.post('/v1/serviceMapping', mappingRequest);

      console.log('Mapping created successfully:', response);

      // Call success callback
      if (onMappingCreated && typeof onMappingCreated === 'function') {
        onMappingCreated(response);
      }

      // Close dialog
      onClose();
    } catch (error) {
      console.error('Error creating service mapping:', error);

      // Extract error message
      let errorMessage = 'Failed to create service mapping. Please try again.';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningAmberIcon color="warning" />
          <Typography variant="h6">Service Not Mapped to Partner</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            The service <strong>{service?.serviceName || 'N/A'}</strong> is not yet mapped to partner <strong>{partner?.organisation?.name || partner?.name || 'N/A'}</strong>.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Please enter the partner's pricing details below to create the mapping and proceed with ticket assignment.
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Service: {service?.serviceName || 'N/A'}
          </Typography>

          {serviceDetailsError && (
            <Alert severity="info">{serviceDetailsError}</Alert>
          )}

          {serviceDetails && (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Default Service Charges
              </Typography>
              <Typography variant="body2">
                Govt: AED {parseFloat(serviceDetails.govtCharge || 0).toFixed(2)}
              </Typography>
              <Typography variant="body2">
                Service (Excl. Tax): AED {parseFloat(serviceDetails.serviceCharge || 0).toFixed(2)}
              </Typography>
              <Typography variant="body2">
                Tax: AED {parseFloat(serviceDetails.taxOnServiceCharge || 0).toFixed(2)}
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                Service (Incl. Tax): AED {parseFloat(calculateInclusiveFromServiceAndTax(serviceDetails.serviceCharge, serviceDetails.taxOnServiceCharge) || 0).toFixed(2)}
              </Typography>
            </Box>
          )}

          <TextField
            select
            label="Charge Type"
            fullWidth
            value={chargeMode}
            onChange={(e) => {
              const next = e.target.value;
              setChargeMode(next);
              setError(null);

              if (next === 'FIXED') {
                setPercentage('');
                setServiceChargeIncTax('');
                setServiceCharge('');
                setTaxOnServiceCharge('');
                return;
              }

              if (next === 'PERCENTAGE') {
                setServiceChargeIncTax('');
                setServiceCharge('');
                setTaxOnServiceCharge('');
              }
            }}
            disabled={submitting}
          >
            <MenuItem value="FIXED">FIXED</MenuItem>
            <MenuItem value="PERCENTAGE">PERCENTAGE</MenuItem>
          </TextField>

          {chargeMode === 'PERCENTAGE' && (
            <TextField
              label="Percentage"
              fullWidth
              value={percentage}
              onChange={handlePercentageChange}
              disabled={submitting || serviceDetailsLoading || !serviceDetails}
              placeholder="Enter percentage"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              helperText={
                !serviceDetails
                  ? 'Default service charges are required for percentage calculation'
                  : 'Calculated from default service charge (incl. tax)'
              }
            />
          )}

          <TextField
            label="Service Charge"
            fullWidth
            required
            value={serviceChargeIncTax}
            onChange={handleServiceChargeIncTaxChange}
            disabled={submitting || chargeMode === 'PERCENTAGE'}
            placeholder="Enter service charge"
            InputProps={{
              startAdornment: <InputAdornment position="start">AED</InputAdornment>,
            }}
            helperText={chargeMode === 'PERCENTAGE' ? 'Calculated (including tax)' : 'Including tax'}
          />

          <TextField
            label="Tax On Service Charge"
            fullWidth
            value={taxOnServiceCharge}
            disabled
            InputProps={{
              startAdornment: <InputAdornment position="start">AED</InputAdornment>,
            }}
          />

          <TextField
            label="Service Charge (Excl. Tax)"
            fullWidth
            value={serviceCharge}
            disabled
            InputProps={{
              startAdornment: <InputAdornment position="start">AED</InputAdornment>,
            }}
          />

          <TextField
            label="Government Charge"
            fullWidth
            value={govtCharge.toFixed(2)}
            disabled
            InputProps={{
              startAdornment: <InputAdornment position="start">AED</InputAdornment>,
            }}
            helperText="Government fee (reference only, same as customer pays)"
          />

          <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Partner Payment Breakdown:
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Partner Service Charge: AED {parseFloat(serviceCharge || 0).toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
              Government Charge: AED {parseFloat(govtCharge || 0).toFixed(2)} (reference only)
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" fontWeight="600">
              Total Payment to Partner: AED {parseFloat(serviceChargeIncTax || 0).toFixed(2)}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Note: Total payment to partner includes service charge + tax
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleCreateMapping}
          variant="contained"
          color="primary"
          disabled={submitting || !serviceCharge}
        >
          {submitting ? <CircularProgress size={24} /> : 'Save Mapping & Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceMappingDialog;
